import apiService, { buildQueryString } from './api';
class VendasService {
    // Listar vendas
    async list(filters = {}) {
        const queryString = buildQueryString(filters);
        const response = await apiService.get(`/vendas${queryString}`);
        return {
            vendas: response.vendas || [],
            pagination: response.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: 20,
                hasNext: false,
                hasPrev: false
            }
        };
    }
    // Buscar venda por ID
    async getById(id) {
        const response = await apiService.get(`/vendas/${id}`);
        return response.data;
    }
    // Criar venda
    async create(data) {
        const response = await apiService.post('/vendas', data);
        return response.data;
    }
    // Cancelar venda
    async cancel(id, motivo) {
        await apiService.post(`/vendas/${id}/cancel`, { motivo });
    }
    // Gerar documento (recibo/fatura)
    async gerarDocumento(id, tipo) {
        const queryString = tipo ? `?tipo=${tipo}` : '';
        return await apiService.generatePdf(id, tipo || 'RECIBO');
    }
    // Obter estatísticas
    async getStats() {
        const response = await apiService.get('/vendas/stats');
        return response.data;
    }
    // Exportar vendas
    async exportar(format, filters = {}) {
        const queryString = buildQueryString({ ...filters, format });
        await apiService.download(`/vendas/export${queryString}`, `vendas.${format}`);
    }
    // Buscar vendas recentes
    async getRecentes(limit = 10) {
        const response = await apiService.get(`/vendas?limit=${limit}`);
        return response.vendas || [];
    }
    // Buscar vendas por cliente
    async getByCliente(clienteId) {
        const response = await apiService.get(`/vendas?clienteId=${clienteId}&limit=100`);
        return response.vendas || [];
    }
    // Buscar vendas por período
    async getByPeriodo(dataInicio, dataFim) {
        const response = await apiService.get(`/vendas?dataInicio=${dataInicio}&dataFim=${dataFim}&limit=1000`);
        return response.vendas || [];
    }
    // Validar venda antes de criar
    async validarVenda(data) {
        const erros = [];
        // Validar itens
        if (!data.itens || data.itens.length === 0) {
            erros.push('Deve ter pelo menos um item');
        }
        // Validar pagamentos
        if (!data.pagamentos || data.pagamentos.length === 0) {
            erros.push('Deve ter pelo menos uma forma de pagamento');
        }
        // Validar total dos pagamentos
        if (data.pagamentos && data.pagamentos.length > 0) {
            const totalPagamentos = data.pagamentos.reduce((acc, pag) => acc + pag.valor, 0);
            const totalVenda = this.calcularTotalVenda(data);
            if (Math.abs(totalPagamentos - totalVenda) > 0.01) {
                erros.push(`Total dos pagamentos (€${totalPagamentos.toFixed(2)}) não confere com o total da venda (€${totalVenda.toFixed(2)})`);
            }
        }
        // Validar quantidades
        for (const item of data.itens) {
            if (item.quantidade <= 0) {
                erros.push(`Quantidade inválida para o produto ${item.produtoId}`);
            }
            if (item.precoUnitario < 0) {
                erros.push(`Preço inválido para o produto ${item.produtoId}`);
            }
        }
        return { valida: erros.length === 0, erros: erros.length > 0 ? erros : undefined };
    }
    // Calcular total da venda
    calcularTotalVenda(data) {
        let subtotal = 0;
        // Somar itens
        for (const item of data.itens) {
            const subtotalItem = item.quantidade * item.precoUnitario;
            const descontoItem = (subtotalItem * item.desconto) / 100;
            subtotal += subtotalItem - descontoItem;
        }
        // Aplicar desconto geral
        const descontoGeral = (subtotal * data.descontoGeral) / 100;
        const subtotalComDesconto = subtotal - descontoGeral;
        // Aplicar IVA (23% em Portugal)
        const valorIva = (subtotalComDesconto * 23) / 100;
        return subtotalComDesconto + valorIva;
    }
    // Calcular subtotais e descontos
    calcularDetalhesVenda(data) {
        let subtotal = 0;
        const itensDetalhes = [];
        // Calcular cada item
        for (const item of data.itens) {
            const subtotalItem = item.quantidade * item.precoUnitario;
            const descontoItem = (subtotalItem * item.desconto) / 100;
            const totalItem = subtotalItem - descontoItem;
            subtotal += subtotalItem;
            itensDetalhes.push({
                ...item,
                subtotal: subtotalItem,
                valorDesconto: descontoItem,
                total: totalItem
            });
        }
        // Calcular desconto geral
        const descontoGeral = (subtotal * data.descontoGeral) / 100;
        const subtotalComDesconto = subtotal - descontoGeral;
        // Calcular IVA
        const ivaPercentual = 23;
        const valorIva = (subtotalComDesconto * ivaPercentual) / 100;
        const total = subtotalComDesconto + valorIva;
        return {
            itens: itensDetalhes,
            subtotal,
            descontoGeral: data.descontoGeral,
            valorDescontoGeral: descontoGeral,
            subtotalComDesconto,
            ivaPercentual,
            valorIva,
            total
        };
    }
}
// Instância singleton
const vendasService = new VendasService();
export default vendasService;
// Utilitários
export const formasPagamento = [
    { value: 'DINHEIRO', label: 'Dinheiro', icon: '💵', requiresReference: false },
    { value: 'MBWAY', label: 'MB WAY', icon: '📱', requiresReference: true },
    { value: 'MULTIBANCO', label: 'Multibanco', icon: '🏧', requiresReference: true },
    { value: 'TRANSFERENCIA', label: 'Transferência Bancária', icon: '🏦', requiresReference: true }
];
export const tiposDocumento = [
    { value: 'RECIBO', label: 'Recibo', description: 'Documento simples para vendas' },
    { value: 'FATURA', label: 'Fatura', description: 'Documento fiscal com IVA discriminado' }
];
export const getFormaPagamentoConfig = (formaPagamento) => {
    return formasPagamento.find(f => f.value === formaPagamento) || formasPagamento[0];
};
export const getTipoDocumentoConfig = (tipoDocumento) => {
    return tiposDocumento.find(t => t.value === tipoDocumento) || tiposDocumento[0];
};
export const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR'
    }).format(valor);
};
export const formatarQuantidade = (quantidade) => {
    return new Intl.NumberFormat('pt-PT').format(quantidade);
};
export const formatarPorcentagem = (valor) => {
    return new Intl.NumberFormat('pt-PT', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 2
    }).format(valor / 100);
};
export const calcularDesconto = (valor, percentual) => {
    return (valor * percentual) / 100;
};
export const calcularIVA = (valor, percentual = 23) => {
    return (valor * percentual) / 100;
};
export const calcularTotalComIVA = (valor, percentual = 23) => {
    return valor + calcularIVA(valor, percentual);
};
export const gerarNumeroVenda = () => {
    const agora = new Date();
    const ano = agora.getFullYear();
    const timestamp = agora.getTime().toString().slice(-6);
    return `V${ano}-${timestamp}`;
};
export const validarNIF = (nif) => {
    if (!nif || typeof nif !== 'string')
        return false;
    const nifLimpo = nif.replace(/\s/g, '');
    if (!/^\d{9}$/.test(nifLimpo))
        return false;
    const primeiroDigito = parseInt(nifLimpo[0]);
    if (![1, 2, 3, 5, 6, 7, 8, 9].includes(primeiroDigito))
        return false;
    const digitos = nifLimpo.split('').map(Number);
    let soma = 0;
    for (let i = 0; i < 8; i++) {
        soma += digitos[i] * (9 - i);
    }
    const resto = soma % 11;
    const digitoControle = resto < 2 ? 0 : 11 - resto;
    return digitoControle === digitos[8];
};
export const formatarNIF = (nif) => {
    if (!nif)
        return '';
    const nifLimpo = nif.replace(/\s/g, '');
    if (nifLimpo.length === 9) {
        return `${nifLimpo.slice(0, 3)} ${nifLimpo.slice(3, 6)} ${nifLimpo.slice(6)}`;
    }
    return nif;
};
export const validarEmail = (email) => {
    if (!email)
        return false;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
};
export const formatarTelefone = (telefone) => {
    if (!telefone)
        return '';
    const telefoneLimpo = telefone.replace(/[\s\-\(\)]/g, '');
    if (telefoneLimpo.length === 9) {
        return `${telefoneLimpo.slice(0, 3)} ${telefoneLimpo.slice(3, 6)} ${telefoneLimpo.slice(6)}`;
    }
    return telefone;
};
